import { Logger } from '@vp/ubik-logging'

// Type definitions for window tracking
declare global {
  interface Window {
    tracking?: {
      page: (page: PageData) => void
      track: (eventName: string, details: Record<string, unknown>) => void
    }
  }
}

/**
 * PageData interface
 */
export interface PageData {
  /** Of the format productName:pageSection */
  pageName: string
  /** Section of the page, which will corresponds to the functional side of it */
  pageSection: string
  /** Stage of the page, which corresponds to what we want the customer do in the website */
  pageStage: string
}

export interface TrackingDetails {
  category?: string
  label?: string
  pageName?: string
  pageSection?: string
  pageStage?: string
  eventDetail?: string
  navigationDetail?: string
  subject?: string
  genTrack?: string
  [key: string]: unknown
}

export interface NavigationDetails {
  destinationPagePath: string
  pageZone: string
  ctaValue: string
}

export default class AnalyticsTracking {
  /**
   * Sends the Page event
   */
  static sendPageTracking(page: PageData, logger?: Logger): void {
    AnalyticsTracking.sendWindowEvent(
      () => {
        window.tracking?.page(page)
      },
      0,
      logger,
    )
  }

  static sendWindowEvent(event: () => void, breaker: number, logger?: Logger): void {
    if (window?.tracking?.page) {
      event()
    } else if (breaker > 20) {
      logger?.error(new Error('Tracking not loaded after 20 seconds'), {
        component: 'AnalyticsTracking',
        context: 'UBIK Help Center - Tracking',
      })
    } else {
      setTimeout(() => this.sendWindowEvent(event, ++breaker, logger), 1000)
    }
  }

  static trackEvent(eventName: string, subject: string, logger?: Logger): void {
    AnalyticsTracking.sendEvent(
      () => {
        window.tracking?.track(eventName, {
          subject,
          pageName: 'Help Center',
          pageSection: 'Help Center:Article Page',
          pageStage: 'Customer Care',
          genTrack: 'Titans Squad',
        })
      },
      0,
      logger,
    )
  }

  static trackNavigationClicked(eventName: string, details: NavigationDetails, logger?: Logger): void {
    AnalyticsTracking.sendEvent(
      () => {
        const sourcePagePath = window.location.pathname
        const eventDetail = `${sourcePagePath};${details.destinationPagePath};${details.pageZone};${details.ctaValue}`
        window.tracking?.track('Navigation Clicked', {
          category: 'Help Center',
          label: `Content:${details.pageZone}`,
          eventDetail,
          navigationDetail: `${details.ctaValue}`,
          pageSection: 'Help Center:Article Page',
          pageStage: 'Customer Care',
          pageName: 'Help Center',
        })
      },
      0,
      logger,
    )
  }

  static trackEventWithDetails(eventName: string, details: TrackingDetails, logger?: Logger): void {
    AnalyticsTracking.sendEvent(
      () => {
        window.tracking?.track(eventName, details)
      },
      0,
      logger,
    )
  }

  static sendEvent(event: () => void, breaker: number, logger?: Logger): void {
    if (window?.tracking?.track) {
      event()
    } else if (breaker > 20) {
      logger?.error(new Error('Tracking not loaded after 20 seconds'), {
        component: 'AnalyticsTracking',
        context: 'UBIK Help Center - Tracking',
      })
    } else {
      setTimeout(() => this.sendEvent(event, ++breaker, logger), 1000)
    }
  }

  static getFiscalYear(date: Date): string {
    const month = date.getUTCMonth()
    const year = date.getUTCFullYear()

    return 'FY' + ((month > 5 ? year + 1 : year) % 100)
  }
}
